import React, {useId, useRef, useState} from "react";
import {Box, ButtonBase, Divider, MenuItem, MenuList, PaletteMode, Popover, Typography} from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import FilterAlt from "@mui/icons-material/FilterAlt";
import {useTranslation} from "react-i18next";
import {
    SortDirection,
    sortMenuChoice,
    SortMenuEntry,
    sortMenuSelection
} from "@/common/overview-state/overview-state-model";
import {
    resolveHeaderArrowSx,
    resolveHeaderIndicatorOpacity,
    resolveHeaderLabelSx
} from "@/lib/components/table-header-styles";
import {OverviewTableI18N} from "@/translations";

/**
 * The three menu entries in display order (plan "Spaltenkopf-Filterung und Sortieranzeige", Milestone-
 * Validierung: "Aufsteigend", "Absteigend", "Sortierung aufheben").
 */
const SORT_MENU_ENTRIES: readonly SortMenuEntry[] = ["ASC", "DESC", "NONE"];

export interface DataTableColumnHeaderProps {
    /** The column's rendered header content — plain text or an element like `renderDistanceHeader`. */
    label: React.ReactNode;
    /** Current MUI palette mode, drives the contrast-color fragments from `table-header-styles.ts`. */
    mode: PaletteMode;
    /** `meta.sortField` of THIS column. `undefined` renders the filter-only variant (Standing constraint 1,
     *  "Ohne sortField erscheint kein Sortierabschnitt"). */
    columnSortField?: string;
    /** The table's currently applied sort field (`DataTable`'s own `sortField` prop) — compared against
     *  `columnSortField` to decide whether THIS column is the active one. */
    appliedSortField?: string;
    appliedSortDirection?: SortDirection;
    /** Mirrors `DataTable#onSortChange` — called with the pair `sortMenuChoice` produces for the clicked entry.
     *  `undefined` on a table with no sorting wired up at all; then `columnSortField` is also always `undefined`
     *  (`DataTable`'s own guard), so the sort section never renders and this is never called. */
    onSortChange?: (field: string | undefined, direction: SortDirection | undefined) => void;
    /** Already resolved via `DataTable`'s `renderColumnFilter?.(columnId)` (Standing constraint 2) — presence
     *  enables the filter section, rendered OUTSIDE the `MenuList` (Standing constraint 1). Absence renders
     *  the sort-only variant ("Ohne Filterinhalt erscheint nur der Sortierabschnitt"). */
    filterContent?: React.ReactNode;
    /** Whether this column currently carries a deviating filter — drives the (aria-hidden) filter icon
     *  (Standing constraint 11). */
    hasActiveFilter?: boolean;
}

/**
 * Column-header dropdown trigger and popup shared by `DataTable`'s request and order overview variants
 * (plan "Spaltenkopf-Filterung und Sortieranzeige", Phase 1.2). Replaces `TableSortLabel`'s three-click
 * ring: a real `button` opens a `Popover` holding a `MenuList` with the three sort entries plus, once
 * Milestone 2/3 wire it up, the column's filter controls below a `Divider` — OUTSIDE the `MenuList`.
 *
 * Not a MUI `Menu`: `MenuList` attaches its own `handleKeyDown` to the `role="menu"` element and calls
 * `preventDefault()` on every single-character key once its type-ahead search kicks in (checked against
 * `@mui/material/MenuList/MenuList.js`), so an input field placed inside a `MenuList` would lose every
 * keystroke. `Tab` is untouched by `MenuList`, so it still carries focus out of the list and into the
 * filter area below.
 *
 * Stacking (Standing constraint 6): this `Popover` needs no manual `zIndex` offset. A component mounted
 * unconditionally, its container element always present in the tree with `open` only toggling visibility,
 * would create its portal in `document.body` as soon as it first mounts, possibly BEFORE
 * `FullscreenTableDialog`'s own portal — equal `zIndex.modal` then falls back to DOM order, and the
 * earlier-mounted portal would paint BELOW the dialog. This `Popover`, in contrast, is only ever rendered
 * while `open` is `true` (MUI's `Modal` returns `null` otherwise, see `useModal.js`), so its portal is
 * created strictly AFTER an already-open `FullscreenTableDialog`'s — same default `zIndex.modal`, later DOM
 * position wins. Verified interactively (Playwright, fullscreen dialog opened first, then a sortable
 * header's dropdown): the dropdown paints above the dialog with no offset. Escape closing only the topmost
 * overlay needs no extra wiring either — `useModal.js`'s `isTopModal()` check already gates every `Modal`'s
 * own Escape handling to the topmost one in the stack.
 */
export const DataTableColumnHeader: React.FC<DataTableColumnHeaderProps> = ({
    label,
    mode,
    columnSortField,
    appliedSortField,
    appliedSortDirection,
    onSortChange,
    filterContent,
    hasActiveFilter = false
}: DataTableColumnHeaderProps): React.ReactElement => {
    const {t} = useTranslation();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuItemRefs = useRef<Array<HTMLLIElement | null>>([]);
    const [open, setOpen] = useState(false);
    // Stable across renders, unique per rendered column header (one `DataTableColumnHeader` instance per
    // sortable/filterable column), so `aria-controls` on the trigger names exactly this instance's own
    // `Popover` paper regardless of how many other column headers are open in the same table.
    const popoverId = useId();

    const hasSortSection = columnSortField !== undefined;
    const hasFilterSection = filterContent !== undefined;
    const isSortActive = hasSortSection && appliedSortField === columnSortField;
    const activeEntry: SortMenuEntry = hasSortSection
        ? sortMenuSelection({sortField: appliedSortField, sortDirection: appliedSortDirection}, columnSortField)
        : "NONE";

    const labelSx = resolveHeaderLabelSx(mode);
    const arrowSx = resolveHeaderArrowSx(mode, isSortActive);
    const indicatorOpacity = resolveHeaderIndicatorOpacity();
    const ArrowIcon = isSortActive && appliedSortDirection === "DESC" ? ArrowDownward : ArrowUpward;

    const sortEntryLabel = (entry: SortMenuEntry): string => {
        if (entry === "ASC") {
            return t(OverviewTableI18N.sort.sortAscending);
        }
        if (entry === "DESC") {
            return t(OverviewTableI18N.sort.sortDescending);
        }
        return t(OverviewTableI18N.sort.resetToDefault);
    };

    const handleOpen = (): void => setOpen(true);

    const handleClose = (): void => setOpen(false);

    const handleSelectEntry = (entry: SortMenuEntry): void => {
        if (columnSortField === undefined) {
            return;
        }
        const next = sortMenuChoice(columnSortField, entry);
        onSortChange?.(next.sortField, next.sortDirection);
        setOpen(false);
    };

    /**
     * Moves focus into the `MenuList` once the `Popover`'s enter transition has finished — the currently
     * checked ASC/DESC entry if the column is actively sorted (mirrors a native `<select>` opening with
     * its selected option focused), otherwise the first entry ("Aufsteigend"). `"NONE"` is deliberately
     * NOT treated as "focus the checked entry": it is always a valid, always-present entry (the array
     * always contains it), so naively focusing `indexOf(activeEntry)` would put focus on "Sortierung
     * aufheben" for every unsorted column — verified interactively (Playwright) against the milestone
     * validation's own keyboard scenario (Tab, Enter, ArrowDown to the SECOND entry, Enter), which only
     * lands on "Absteigend" when the neutral open starts focus at the first entry. Runs after MUI's own
     * `Modal` autofocus, so it wins.
     */
    const handleEntered = (): void => {
        const targetIndex = activeEntry === "NONE" ? 0 : SORT_MENU_ENTRIES.indexOf(activeEntry);
        menuItemRefs.current[targetIndex]?.focus();
    };

    return (
        <>
            {/* No tooltip on the trigger button: a column header carries no static "Filter" or "Sortieren
                und filtern" text any more. A tooltip re-added here needs MUI's `describeChild` on its
                `Tooltip`, otherwise every header button reports the same accessible name (the tooltip
                text replaces it via `aria-label`) and breaks every `columnheader`/`button` lookup by
                column name (`innendienstsicht.spec.ts` and both overview specs). */}
            <ButtonBase
                ref={triggerRef}
                component="button"
                disableRipple
                onClick={handleOpen}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={popoverId}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "2px",
                    font: "inherit",
                    fontWeight: "inherit",
                    whiteSpace: "nowrap",
                    color: labelSx.color,
                    "&:hover": labelSx["&:hover"],
                    "&:hover .data-table-header-sort-arrow": {opacity: 1}
                }}
            >
                {/* `label` renders fully INSIDE this button, so any interactive element embedded in a
                    header's content (icon, tooltip trigger) must stop its own click in the capture
                    phase, or the click bubbles into this `onClick` and opens the dropdown instead of
                    letting the embedded element's own interaction (e.g. its own Tooltip) run.
                    `renderDistanceHeader` (RequestOverviewTable.tsx) and `UnreadTotalHeader`
                    (both overview tables' Info column) embed such an element, each guarded by its own
                    `onClickCapture` (Standing constraint 12). */}
                {label}
                {hasSortSection && (
                    <ArrowIcon
                        aria-hidden
                        className="data-table-header-sort-arrow"
                        sx={{fontSize: 16, color: arrowSx.color, opacity: arrowSx.opacity}}
                    />
                )}
                <ArrowDropDown aria-hidden sx={{fontSize: 18, color: labelSx.color, opacity: indicatorOpacity}} />
                {hasActiveFilter && <FilterAlt aria-hidden sx={{fontSize: 14, color: labelSx.color}} />}
            </ButtonBase>
            <Popover
                open={open}
                anchorEl={triggerRef.current}
                onClose={handleClose}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                slotProps={{paper: {id: popoverId, sx: {minWidth: 220}}}}
                transitionDuration={{enter: 100, exit: 0}}
                TransitionProps={{onEntered: handleEntered}}
            >
                {hasSortSection && (
                    <MenuList role="menu" dense>
                        {SORT_MENU_ENTRIES.map((entry, index) => (
                            <MenuItem
                                key={entry}
                                ref={(el: HTMLLIElement | null) => {
                                    menuItemRefs.current[index] = el;
                                }}
                                role="menuitemradio"
                                aria-checked={entry === activeEntry}
                                selected={entry === activeEntry}
                                onClick={() => handleSelectEntry(entry)}
                            >
                                {sortEntryLabel(entry)}
                            </MenuItem>
                        ))}
                    </MenuList>
                )}
                {hasSortSection && hasFilterSection && <Divider />}
                {hasFilterSection && (
                    <Box sx={{padding: 2, minWidth: 220}}>
                        <Typography variant="subtitle2" sx={{marginBottom: 1}}>
                            {t(OverviewTableI18N.menu.filter)}
                        </Typography>
                        {filterContent}
                    </Box>
                )}
            </Popover>
        </>
    );
};
